import { useState } from "react";
import { BondFieldRenderer } from "@/components/BondFieldRenderer";
import type { BondFieldValue } from "@/lib/bondFields";
import {
  activeBondPairMode,
  bondPairRequired,
  type BondRenderUnit,
  type BondPairMode,
} from "@/lib/bondFieldPairs";
import type { BondValueBag } from "@/lib/bondFields";

// BRK-S169 — the composite over a DECLARED pair of configured fields: one
// labelled input with a unit toggle that decides which of the two it writes.
//
// ── WHY IT IS A WRAPPER AND NOT A BRANCH IN BondFieldRenderer ───────────────
//
// `BondFieldRenderer` maps a field's declared TYPE onto a control and may never
// branch on a product, a subproduct or a field NAME — that is the rule the whole
// config-driven flow rests on. Pairing is not a type; it is a statement about
// two particular fields, which today lives in a BRK-side table (a filed contract
// gap — see `lib/bondFieldPairs.ts`).
//
// So the table is consulted HERE, and the renderer is handed the ACTIVE
// member's own field, untouched. Every control decision stays where it was:
// `bondAmount` still draws the currency input with its grouping and `$`,
// `bidBondPenaltyPercent` still draws the percent input with its own 0–100
// bounds, and both still take their format rules, their inline error slot and
// their review marker from the one renderer. Nothing about this composite has
// to be re-implemented per control.

export function BondPairedField({
  unit,
  values,
  onChange,
  invalid,
  pendingReview,
}: {
  unit: Extract<BondRenderUnit, { kind: "pair" }>;
  /** The whole step bag — the composite reads BOTH members to decide its mode. */
  values: BondValueBag;
  onChange: (name: string, value: BondFieldValue | undefined) => void;
  /** Reason per field name, client-side and server-side alike. */
  invalid: Record<string, string>;
  pendingReview: (name: string) => boolean;
}) {
  const { pair, fields } = unit;
  /**
   * The mode the BROKER picked this visit, or null while nobody has.
   *
   * Necessary, and the reason is the one thing a purely derived mode cannot
   * express: switching to $ CLEARS the percentage, so a mode derived from the
   * values alone would find neither member answered the instant after the
   * switch and snap straight back to the default. The toggle would visibly
   * refuse to move.
   *
   * Null is the resume state, and it is null on every mount: there is no
   * client-side persistence in this flow (NN #9), so landing on the step —
   * first time or after a reload — derives the mode from whichever member the
   * ORDER holds a value for.
   */
  const [chosenModeId, setChosenModeId] = useState<string | null>(null);
  const active =
    pair.modes.find((mode) => mode.id === chosenModeId) ??
    activeBondPairMode(unit, values);
  const activeIndex = pair.modes.findIndex((mode) => mode.id === active.id);
  const activeField = fields[activeIndex === -1 ? 0 : activeIndex];

  /**
   * Switch mode, CLEARING the other member.
   *
   * The clear is the point of the whole control: a penal sum stated both as a
   * percentage and as a figure is a penal sum nobody can rely on, and only one
   * of the two ever reaches the filing. Clearing on the SWITCH rather than at
   * save time means what the broker sees is what is stored — an amount that
   * vanished silently at submit is the version of this nobody could debug.
   */
  function selectMode(mode: BondPairMode) {
    if (mode.id === active.id) return;
    setChosenModeId(mode.id);
    for (const other of pair.modes) {
      if (other.id !== mode.id) onChange(other.key, undefined);
    }
  }

  // EITHER member's reason (BRK-S169). The server validates the two fields
  // separately and knows nothing about the composite, so a refusal naming the
  // field the broker is NOT currently looking at still has to land on the one
  // control that writes it — otherwise it lands nowhere and the broker is told
  // the step failed with no indication where.
  const error =
    invalid[activeField.name] ??
    pair.modes
      .map((mode) => invalid[mode.key])
      .find((reason) => reason !== undefined);

  // Two buttons rather than a select: two options with one-character labels,
  // and the choice has to be visible beside the number it describes.
  // `aria-pressed` carries the state, and the accessible name is the mode's
  // sentence — "%" alone reads as punctuation.
  //
  // THE INPUT'S SUFFIX (BRK-S181), inside its box and in place of the static
  // `%` / `$` — one unit indicator, not two. It used to sit on a row of its own
  // above the label, which pushed Bond penalty's label and input down out of
  // line with Estimated contract price beside it.
  const toggle = (
    <span className="flex overflow-hidden rounded border border-brk-border">
      {pair.modes.map((mode) => (
        <button
          key={mode.id}
          type="button"
          aria-pressed={mode.id === active.id}
          aria-label={mode.description}
          onClick={() => selectMode(mode)}
          className={`px-2.5 py-1 text-sm font-medium transition-colors ${
            mode.id === active.id
              ? "bg-brk-accent text-white"
              : "bg-brk-panel text-brk-dim hover:bg-brk-bg"
          }`}
        >
          {mode.symbol}
        </button>
      ))}
    </span>
  );

  return (
    // Named by the pair, NOT by a visible heading: the label a broker reads is
    // the input's own, and rendering it twice over one number is how a control
    // starts looking like two.
    <div role="group" aria-label={pair.label}>
      {/* The ACTIVE member's own field, unchanged — so the currency control, the
          percent bounds, the format rules and the review marker all come from
          the one renderer rather than being restated here. Its own label is
          suppressed in favour of the composite's: two labels over one input is
          how a broker ends up reading "Bid bond penalty %" above a dollar box.
          Its help is suppressed too (BRK-S181): the toggle says what the one
          number is, and a sentence under it was a second way of saying so. */}
      <BondFieldRenderer
        field={{
          ...activeField,
          label: pair.label,
          help: undefined,
          // Required as a WHOLE (BRK-S289): the pair's own declaration, since
          // an at-least-one-of rule cannot be stated on either member.
          required: bondPairRequired(unit),
        }}
        value={values[activeField.name]}
        onChange={(value) => onChange(activeField.name, value)}
        error={error}
        pendingReview={pendingReview(activeField.name)}
        unitControl={toggle}
      />
    </div>
  );
}
